// A version's files as the Review tab lists them, taking one off a version, an earlier cut a new draft replaces, and
// Download All (Ryan, 2026-10-12: a client's final list held ten films where four were final, and its 53 GB zip could
// not arrive). Pure: components/VideoReview.js and PhotoDeliverables.js do the asking and the showing.
//
// The server is the authority for each rule (server.py delete_version_file and the CUTS DISPOSITION GUARD;
// routes/photo_review.py for the zip). This file is their mirror on the page, tested alone (finalFiles.test.mjs).

// ── the list ────────────────────────────────────────────────────────────────────────────────────────────────
// The version's own films first, then every version's project files and assets, once each (they are owed to the
// client whichever version they were sent with). Each row keeps the version that lists it as `_v`: a row is taken off
// THAT version, which for a project file or an asset is often not the one on screen.
export function listed(versions, currentV) {
  const ver = (versions || []).find(v => v.version_number === currentV);
  const films = (ver?.files || []).filter(f => f.type === 'review_video').map(f => ({ ...f, _v: currentV }));
  const seen = new Set();
  const rest = [];
  for (const v of versions || []) {
    for (const f of (v.files || v.deliverable_links || [])) {
      if (f.type !== 'review_video' && f.file_id && !seen.has(f.file_id)) {
        seen.add(f.file_id);
        rest.push({ ...f, _v: v.version_number });
      }
    }
  }
  return [...films, ...rest];
}

// The list in its two groups (Ryan, 2026-10-13): the films, each downloaded on its own straight from storage with
// nothing to prepare, and the rest (project files, assets: many and small), which is what a zip is good for.
// `restZip` says whether "the rest as one zip" is worth a button of its own: only beside films (with none, it is
// Download All) and only for more than one file.
export function split(files) {
  const films = (files || []).filter(f => f.type === 'review_video');
  const rest = (files || []).filter(f => f.type !== 'review_video');
  return { films, rest, restZip: films.length > 0 && rest.length > 1 };
}

export const FILMS_TITLE = 'Films';
export const FILMS_HINT = 'Each film downloads on its own, right away. There is nothing to prepare.';
export const REST_TITLE = 'Project files and assets';
export const REST_ZIP = 'Download these as one ZIP';

// How much there is to download, in bytes (0 when no row says its size).
export function totalBytes(files) {
  return (files || []).reduce((n, f) => n + (Number(f.size) > 0 ? Number(f.size) : 0), 0);
}

export function sizeWords(bytes) {
  if (!(bytes > 0)) return '';
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// ── taking a file off a version ─────────────────────────────────────────────────────────────────────────────
// The request names the file by what it IS: its file_id, or for a row without one its storage key, or its name. The
// place in the list (`i`) rides along because the route's address has one, and the server trusts it only for a row
// that has neither id nor key. A place alone once removed a different file than the row confirmed.
export function removal(projectId, f, i) {
  const params = f.file_id ? { file_id: f.file_id } : f.key ? { key: f.key } : { name: f.name || f.filename || '' };
  return { url: `/projects/${projectId}/versions/${f._v}/files/${i}`, params };
}

export function removeAsk(f) {
  return `Take "${f.name || f.filename || 'this file'}" off V${f._v}'s list? If another version still lists the same file, `
    + 'the file itself is kept there. If none does, the file is deleted for good.';
}

// What to say once it is done, from the server's answer ({ removed, storage: 'kept' | 'removed' }).
export function removedWords(f, answer) {
  const name = f.name || f.filename || 'The file';
  return answer?.storage === 'kept'
    ? `${name} is off V${f._v}. The file itself is kept: another version still lists it.`
    : `${name} is off V${f._v}, and the file is deleted.`;
}

// The server's own words when it refuses (a page loaded before this change names no file, and is told to reload).
export function removeRefusal(error) {
  const d = error?.response?.data?.detail;
  if (d && typeof d === 'object' && typeof d.message === 'string' && d.message) return d.message;
  return typeof d === 'string' && d ? d : 'That file could not be taken off. Reload the page and try again.';
}

// ── an earlier cut, and what a new draft does with it ───────────────────────────────────────────────────────
// The new films of this draft a cut can be replaced by, in the form's own order: the main video (unless it is carried
// unchanged), then each added cut. `key` is the form's upload key ('video', 'cut_...').
export function freshFilms({ mainFile, mainUnchanged, mainLabel, extraCuts }) {
  const out = [];
  if (mainFile && !mainUnchanged) out.push({ key: 'video', label: (mainLabel || '').trim() || mainFile.name || 'Main video' });
  for (const c of extraCuts || []) out.push({ key: c.key, label: (c.label || '').trim() || c.file?.name || 'New cut' });
  return out;
}

// For each extra cut of the previous version, how this draft disposes of it. `ok` is the server's rule: carried
// unchanged, replaced by a new cut of the SAME label, or replaced by a new film the uploader chose (`replacedBy`:
// { earlier file_id: upload key }, counted only while that film is still in the form and the cut is not also ticked
// unchanged). A cut that is none of these stops the upload: it cannot just disappear between versions.
export function cutStatus(prevCuts, extraCuts, unchangedIds, replacedBy, fresh) {
  const keys = new Set((fresh || []).map(n => n.key));
  return (prevCuts || []).map(pc => {
    const label = (pc.label || '').trim();
    const unchanged = (unchangedIds || []).includes(pc.file_id);
    const sameLabel = !!label && (extraCuts || []).some(c => (c.label || '').trim().toLowerCase() === label.toLowerCase());
    const chosen = (replacedBy || {})[pc.file_id] || '';
    const by = !unchanged && keys.has(chosen) ? chosen : '';
    return { pc, unchanged, sameLabel, by, ok: unchanged || sameLabel || !!by };
  });
}

// The upload's `replaced_cuts`: { earlier cut's file_id: the new film's file_id }, for each choice whose film has
// finished uploading (`uploaded`: upload key -> { file_id }).
export function replacedPayload(status, uploaded) {
  const out = {};
  for (const s of status || []) {
    const fid = s.by && uploaded?.[s.by]?.file_id;
    if (fid) out[s.pc.file_id] = fid;
  }
  return out;
}

export const CUT_HINT = 'check the box, or choose the new file that replaces it';

// ── Download All ────────────────────────────────────────────────────────────────────────────────────────────
// The archive is built in storage and the browser downloads it from there (a signed link), so there is no time limit
// and a dropped download resumes. It is kept for a day: pressing the button again starts at once.
export const ZIP_STARTED = 'Your download has started. If it stops partway, press the button again: the prepared file is kept for a day, so it starts right away.';
export const ZIP_LOST = 'We lost track of the download while it was being prepared. Press the button again.';
export const ZIP_POLL_TRIES = 6;        // polls that may fail in a row (a network blip, a deploy) before the page says so

// What the progress line says. `job`: { status, phase, done, total, bytes_downloaded, bytes_total, error }.
export function zipLine(job) {
  if (!job) return '';
  if (job.status === 'complete') return 'Download started';
  if (job.status === 'error') return job.error || 'The download could not be prepared. Try again.';
  if (job.phase === 'preparing' || !(job.bytes_total >= 0)) return 'Preparing...';
  if (job.phase === 'finishing') return 'Almost ready...';
  if (job.bytes_total > 0) return `Gathering your files... ${sizeWords(job.bytes_downloaded) || '0 KB'} of ${sizeWords(job.bytes_total)}`;
  return `Gathering your files... ${job.done} of ${job.total}`;
}

// How far along, 0 to 100, or null while there is nothing to measure yet.
export function zipPercent(job) {
  if (!job || job.status === 'error') return null;
  if (job.status === 'complete' || job.phase === 'finishing') return 100;
  if (job.phase === 'preparing' || !(job.bytes_total >= 0)) return null;
  if (job.bytes_total > 0) return Math.min(100, Math.round((job.bytes_downloaded / job.bytes_total) * 100));
  return job.total > 0 ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;
}

// What a failed poll means: the job is gone (the server restarted, or it is not this project's) and must be started
// again, or the request simply failed and the next poll may answer.
export function pollFailure(error) {
  return error?.response?.status === 404 ? 'gone' : 'retry';
}

export const ZIP_POLL_MS = 1000;

// Start the archive for a version (or rejoin the one being made) and follow it until the browser is sent to it.
// Everything it touches is handed in, so the page's two Download All buttons share one way of doing it and it is
// tested without a browser: post/get (the api), go(path) (send the browser to a download), wait(ms), onJob(job) on
// every change, alive() (false once the page has moved on). A job that is gone (the server restarted under it) is
// started again once, and finds the finished archive if there is one; polls that keep failing end in words, never in
// a button that quietly resets. `only: 'rest'` asks for the project files and assets alone (the server picks them: the
// request never names a file). Resolves 'started' | 'error' | 'stopped'.
export async function followZip({ post, get, go, wait, onJob, projectId, version, only = '', alive = () => true, restarts = 1 }) {
  let job;
  try {
    const res = await post(`/projects/${projectId}/deliverables/start-zip`, only ? { version, only } : { version });
    job = { job_id: res.data.job_id, status: 'working', phase: 'preparing', done: 0, total: res.data.total || 0,
            bytes_total: -1, bytes_downloaded: 0, failed_files: [] };
  } catch (e) {
    const d = e?.response?.data?.detail;
    onJob({ status: 'error', error: typeof d === 'string' && d ? d : 'The download could not be started. Try again.', failed_files: [] });
    return 'error';
  }
  onJob(job);
  let misses = 0;
  while (alive()) {
    await wait(ZIP_POLL_MS);
    if (!alive()) return 'stopped';
    try {
      const { data } = await get(`/projects/${projectId}/deliverables/zip-progress/${job.job_id}`);
      // An answer with nothing in it is a failed poll like any other: counted, never a reason to go on asking for ever.
      if (!data || typeof data.status !== 'string') throw new Error('the progress answer was empty');
      misses = 0;
      job = { ...job, ...data };
      onJob(job);
      if (data.status === 'complete') {
        go(`/projects/${projectId}/deliverables/download-zip/${job.job_id}`);
        return 'started';
      }
      if (data.status === 'error') return 'error';
    } catch (e) {
      const gone = pollFailure(e) === 'gone';
      if (gone && restarts > 0) return followZip({ post, get, go, wait, onJob, projectId, version, only, alive, restarts: restarts - 1 });
      misses += 1;
      if (gone || misses >= ZIP_POLL_TRIES) {
        onJob({ ...job, status: 'error', error: ZIP_LOST });
        return 'error';
      }
    }
  }
  return 'stopped';
}
